import { Injectable, inject } from '@angular/core';
import { TaskApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/task/task-api';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import { GrowingStepApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/growing-step/growing-step-api';
import { AdviceApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/advice/advice-api';
import { TaskListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/task-list.response';
import { PlantListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/plant-list.response';
import { GrowingStepListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/cultivation/growing-step-list.response';
import { TaskListRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/location/task-list.request';
import { GrowingStepListRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/cultivation/growing-step-list.request';
import { UpdatePlantRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/location/update-plant.request';
import { TaskState } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/task-state.enum';
import { StorageService } from '../front/storage.service';
import { PosthogService } from '../front/posthog.service';
import { DataStore, SNAPSHOT_ONLY } from './data-store.service';
import { isPlantAction, nextStepOf, withThermalProgress } from '../../utils/garden-todo.util';

/** Les tâches ouvertes d'un jardin tiennent sur une page : une par geste de la quinzaine. */
const TASKS_PAGE_SIZE = 100;

/** Une plante dont l'étape manuelle est finie, et l'étape qu'on lui propose. */
export interface PlantAction {
  plant: PlantListResponse;
  nextStep: GrowingStepListResponse;
}

/** Ce que « Mes tâches » montre : les tâches à faire du jardin, et les plantes qui attendent un geste. */
export interface GardenTodo {
  tasks: TaskListResponse[];
  plantActions: PlantAction[];
  /** Les plantes du jardin, déjà lues pour leurs gestes (le mot du jour part d'elles). */
  plants?: PlantListResponse[];
}

/** Un état choisi pour une tâche : `done` se résout une fois son écriture finie, réussie ou non. */
interface TaskWrite {
  done: Promise<void>;
}

/**
 * « Mes tâches » : les tâches du jardin et les plantes qui attendent un geste,
 * pour l'écran, l'onglet et l'accueil.
 *
 * Les lectures reprennent les clés du potager et de la fiche plante : ouvrir
 * l'un après l'autre ne relit rien. Le jardin est celui de l'app (en-tête
 * d'adresse du SDK). Toute écriture passe par le SDK, qui invalide le cache.
 */
@Injectable({ providedIn: 'root' })
export class GardenTodoStore {
  // Les classes du SDK lisent leur configuration dans un singleton : on les
  // instancie directement, comme `PremiumService`.
  private readonly taskApi = new TaskApi();
  private readonly plantApi = new PlantApi();
  private readonly growingStepApi = new GrowingStepApi();
  private readonly adviceApi = new AdviceApi();
  private readonly data = inject(DataStore);
  private readonly storage = inject(StorageService);
  private readonly posthog = inject(PosthogService);
  /** Par tâche, le dernier état choisi dont l'écriture n'est pas finie. */
  private readonly taskWrites = new Map<string, TaskWrite>();

  /** Les tâches à faire et les plantes à agir ; gardées pour le hors ligne. */
  async load(): Promise<GardenTodo> {
    const lang = this.storage.getCurrentLanguage();
    // `TaskApi.list` est typé avec une copie ancienne de la requête, sans `taskStates`.
    const filters: TaskListRequest = { taskStates: [TaskState.TODO] };
    const [taskPage, plants] = await Promise.all([
      this.data.get(
        `garden/tasks:${lang}`,
        () => this.taskApi.list(filters, 1, TASKS_PAGE_SIZE, lang),
        SNAPSHOT_ONLY,
      ),
      this.plants(lang),
    ]);
    return { tasks: taskPage?.results ?? [], plantActions: await this.plantActions(plants, lang), plants };
  }

  /**
   * Coche (DONE), ignore (SKIP) ou rouvre (TODO) une tâche.
   *
   * Les écritures d'une même tâche partent l'une après l'autre, et seul le
   * dernier choix compte : sur un réseau lent, cocher, « Annuler » puis
   * recocher pourrait sinon finir rouvert, la réouverture arrivant la
   * dernière. Un choix remplacé avant de partir n'est pas envoyé.
   *
   * La promesse rend `true` une fois l'état écrit, `false` s'il a été remplacé
   * par un choix plus récent sans s'écrire ; elle n'échoue que si le dernier
   * choix n'a pas pu s'écrire.
   */
  setTaskState(uid: string, state: TaskState): Promise<boolean> {
    const lang = this.storage.getCurrentLanguage();
    const choice: TaskWrite = { done: Promise.resolve() };
    const isLast = () => this.taskWrites.get(uid) === choice;
    const send = async () => {
      if (!isLast()) return false;
      try {
        await this.taskApi.updateState(uid, { taskState: state }, lang);
        return true;
      } catch (e) {
        if (isLast()) throw e;
        return false;
      }
    };
    const previous = this.taskWrites.get(uid);
    this.taskWrites.set(uid, choice);
    const written = previous ? previous.done.then(send) : send();
    // L'écriture suivante attend celle-ci, qu'elle réussisse ou non.
    choice.done = written
      .catch(() => undefined)
      .then(() => {
        if (isLast()) this.taskWrites.delete(uid);
      });
    return written;
  }

  /** Passe une plante à une étape, et le mesure. */
  async changePlantStep(plantUid: string, step: GrowingStepListResponse): Promise<void> {
    const request = new UpdatePlantRequest();
    request.uid = plantUid;
    request.currentGrowingStepUid = step.uid;
    await this.plantApi.update(request, this.storage.getCurrentLanguage());
    this.posthog.capture('plant_step_changed', { plantUid, newStepUid: step.uid, newStepName: step.name });
  }

  /**
   * L'influence lunaire du jour (« root »), pour qualifier un geste : celle du
   * conseil du jour, que tout compte reçoit, sous la clé de l'accueil (souvent
   * déjà en cache). Best-effort : la lune n'est jamais la raison d'un geste ;
   * sans elle, pas de qualificatif.
   */
  async lunarInfluenceToday(): Promise<string | null> {
    const lang = this.storage.getCurrentLanguage();
    try {
      const advice = await this.data.get(`garden/today:${lang}`, () => this.adviceApi.getToday(lang), { persist: true });
      return advice?.lunar?.influence ?? null;
    } catch (e) {
      console.warn('GardenTodoStore: advice of the day unavailable', e);
      return null;
    }
  }

  /** Les plantes du compte, complétées par leur progression thermique, comme Mon potager. */
  private async plants(lang: string): Promise<PlantListResponse[]> {
    const [page, thermal] = await Promise.all([
      this.data.get(`garden/plants:${lang}`, () => this.plantApi.list({} as any, 0, 500, lang), SNAPSHOT_ONLY),
      // Sans progression thermique, repli sur le calendrier.
      this.data
        .get('garden/thermal-progress', () => this.plantApi.getThermalProgressBatch(), SNAPSHOT_ONLY)
        .catch(() => null),
    ]);
    return withThermalProgress(page?.results ?? [], thermal);
  }

  /**
   * Les plantes qui attendent un geste et ont une étape suivante ; les étapes
   * lues une fois par variété. Une variété dont les étapes manquent (hors ligne,
   * jamais ouverte) ne retire que ses plantes, pas la liste.
   */
  private async plantActions(plants: PlantListResponse[], lang: string): Promise<PlantAction[]> {
    const now = new Date();
    const waiting = plants.filter((plant) => isPlantAction(plant, now));
    const variantUids = [...new Set(waiting.map((plant) => plant.cultivationVariant?.uid).filter((uid): uid is string => !!uid))];
    const stepsByVariant = new Map(
      await Promise.all(
        variantUids.map(async (uid) => {
          const steps = await this.steps(uid, lang).catch((e) => {
            console.warn('GardenTodoStore: failed to load the steps of variant', uid, e);
            return [];
          });
          return [uid, steps] as const;
        }),
      ),
    );
    return waiting.flatMap((plant) => {
      const nextStep = nextStepOf(plant.currentGrowingStep, stepsByVariant.get(plant.cultivationVariant?.uid ?? '') ?? []);
      return nextStep ? [{ plant, nextStep }] : [];
    });
  }

  /** Les étapes d'une variété, sous la clé de la fiche plante. */
  private async steps(variantUid: string, lang: string): Promise<GrowingStepListResponse[]> {
    const request = new GrowingStepListRequest();
    request.cultivationVariantUid = variantUid;
    const page = await this.data.get(
      `garden/steps:${variantUid}:${lang}`,
      () => this.growingStepApi.list(request, 0, 20, lang),
      SNAPSHOT_ONLY,
    );
    return page?.results ?? [];
  }
}
